//@ts-nocheck
import { useState, useEffect, useRef, useCallback } from 'react';
import { useMap } from '@/providers/MapProvider';
import Draw from 'ol/interaction/Draw';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import Overlay from 'ol/Overlay';
import { LineString, Polygon } from 'ol/geom';
import { getArea, getLength } from 'ol/sphere';
import { unByKey } from 'ol/Observable';
import { EventsKey } from 'ol/events';
import { Style, Fill, Stroke, Circle as CircleStyle } from 'ol/style';

export type MeasureType = 'LineString' | 'Polygon';
export type DistanceUnit = 'meters' | 'feet' | 'kilometers' | 'miles';
export type AreaUnit = 'sqm' | 'sqft' | 'sqkm' | 'sqmi' | 'acres';

// Styles
const finalMeasureStyle = new Style({
  fill: new Fill({ color: 'rgba(255, 204, 51, 0.3)' }),
  stroke: new Stroke({ color: '#ffcc33', width: 3 }),
  image: new CircleStyle({ radius: 7, fill: new Fill({ color: '#ffcc33' }) }),
});

const sketchStyle = new Style({
  fill: new Fill({ color: 'rgba(0, 153, 255, 0.2)' }),
  stroke: new Stroke({
    color: 'rgba(0, 153, 255, 0.8)',
    width: 3,
    lineDash: [10, 10],
  }),
  image: new CircleStyle({
    radius: 5,
    stroke: new Stroke({ color: 'rgba(0, 153, 255, 0.8)' }),
    fill: new Fill({ color: 'rgba(0, 153, 255, 0.2)' }),
  }),
});

export const useMeasureTool = (
  distanceUnit: DistanceUnit,
  areaUnit: AreaUnit,
) => {
  const { map } = useMap();
  const [activeMeasureType, setActiveMeasureType] =
    useState<MeasureType | null>(null);

  // Refs to keep track of OL objects
  const drawRef = useRef<Draw | null>(null);
  const sourceRef = useRef<VectorSource | null>(null);
  const layerRef = useRef<VectorLayer<VectorSource> | null>(null);
  const sketchTipRef = useRef<Overlay | null>(null);
  const listenerRef = useRef<EventsKey | null>(null);

  // Timer ref to handle the "double click race condition"
  const restartTimerRef = useRef<NodeJS.Timeout | null>(null);

  // --- 1. Cleanup Helper ---
  const removeInteraction = useCallback(() => {
    if (!map) return;

    // Clear Timer
    if (restartTimerRef.current) clearTimeout(restartTimerRef.current);

    // Remove Draw Interaction
    if (drawRef.current) {
      map.removeInteraction(drawRef.current);
      drawRef.current = null;
    }

    // Remove Sketch Tooltip
    if (sketchTipRef.current) {
      map.removeOverlay(sketchTipRef.current);
      sketchTipRef.current = null;
    }

    // Unsubscribe from geometry changes
    if (listenerRef.current) {
      unByKey(listenerRef.current);
      listenerRef.current = null;
    }

    // Reset Cursor
    map.getTargetElement().style.cursor = '';
  }, [map]);

  // --- 2. Formatters ---
  const formatLength = useCallback(
    (line: LineString) => {
      const length = getLength(line, { projection: 'EPSG:3857' });
      switch (distanceUnit) {
        case 'feet':
          return `${(length * 3.28084).toFixed(2)} ft`;
        case 'miles':
          return `${(length * 0.000621371).toFixed(2)} mi`;
        case 'kilometers':
          return `${(length / 1000).toFixed(2)} km`;
        default:
          return `${length.toFixed(2)} m`;
      }
    },
    [distanceUnit],
  );

  const formatArea = useCallback(
    (polygon: Polygon) => {
      const area = getArea(polygon, { projection: 'EPSG:3857' });
      switch (areaUnit) {
        case 'sqft':
          return `${(area * 10.7639).toFixed(2)} ft²`;
        case 'acres':
          return `${(area * 0.000247105).toFixed(2)} ac`;
        case 'sqmi':
          return `${(area * 3.861e-7).toFixed(2)} mi²`;
        case 'sqkm':
          return `${(area / 1e6).toFixed(2)} km²`;
        default:
          return `${area.toFixed(2)} m²`;
      }
    },
    [areaUnit],
  );

  // --- 3. Start Tool ---
  const startMeasuring = useCallback(
    (type: MeasureType) => {
      if (!map) return;

      // A. Clean up previous
      removeInteraction();
      setActiveMeasureType(type);

      // B. Ensure Layer Exists
      let source = sourceRef.current;
      if (!source) {
        // Check if layer already exists on map (re-hook logic)
        const existingLayer = map
          .getLayers()
          .getArray()
          .find((l) => l.get('id') === 'measure-layer');
        if (existingLayer instanceof VectorLayer) {
          source = existingLayer.getSource();
          sourceRef.current = source;
          layerRef.current = existingLayer as VectorLayer<VectorSource>;
        } else {
          source = new VectorSource();
          const layer = new VectorLayer({
            source: source,
            style: finalMeasureStyle,
            zIndex: 1000,
            properties: { id: 'measure-layer' },
          });
          map.addLayer(layer);
          sourceRef.current = source;
          layerRef.current = layer;
        }
      }

      // C. Create Interaction
      const draw = new Draw({
        source: source!,
        type: type,
        style: sketchStyle,
      });

      map.addInteraction(draw);
      drawRef.current = draw;
      map.getTargetElement().style.cursor = 'crosshair';

      // D. Create Tooltip
      const tooltipDiv = document.createElement('div');
      tooltipDiv.className = 'ol-tooltip ol-tooltip-measure';
      const tooltip = new Overlay({
        element: tooltipDiv,
        offset: [0, -15],
        positioning: 'bottom-center',
        stopEvent: false, // Critical for clicking through
      });
      map.addOverlay(tooltip);
      sketchTipRef.current = tooltip;

      // E. Events
      draw.on('drawstart', (evt) => {
        const sketch = evt.feature;
        let tooltipCoord = evt.coordinate;

        listenerRef.current = sketch.getGeometry()!.on('change', (e) => {
          const geom = e.target;
          let output = '';
          if (geom instanceof Polygon) {
            output = formatArea(geom as Polygon);
            tooltipCoord = (geom as Polygon)
              .getInteriorPoint()
              .getCoordinates();
          } else if (geom instanceof LineString) {
            output = formatLength(geom as LineString);
            tooltipCoord = (geom as LineString).getLastCoordinate();
          }
          tooltipDiv.innerHTML = output;
          tooltip.setPosition(tooltipCoord);
        });
      });

      draw.on('drawend', () => {
        // 1. Finalize Tooltip style
        tooltipDiv.className = 'ol-tooltip ol-tooltip-static';
        tooltipDiv.style.backgroundColor = 'rgba(255,255,255,0.9)';
        tooltipDiv.style.border = '1px solid #777';
        tooltipDiv.style.padding = '2px 5px';
        tooltipDiv.style.borderRadius = '4px';
        tooltipDiv.style.fontWeight = 'bold';
        tooltip.setOffset([0, -7]);

        // Detach ref so cleanup doesn't remove the finished label
        sketchTipRef.current = null;

        // 2. Stop current interaction immediately to prevent double-click leak
        removeInteraction();

        // 3. Restart after delay (The Fix for "Sometimes doesn't stop")
        restartTimerRef.current = setTimeout(() => {
          startMeasuring(type);
        }, 300); // 300ms delay to swallow the second click of a double-click
      });
    },
    [map, removeInteraction, formatLength, formatArea],
  );

  // --- 4. Stop Public ---
  const stopMeasuring = useCallback(() => {
    removeInteraction();
    setActiveMeasureType(null);
  }, [removeInteraction]);

  // --- 5. Clear Public ---
  const clearMeasurements = useCallback(() => {
    stopMeasuring();

    // Clear Vectors
    sourceRef.current?.clear();

    // Clear Static Overlays manually

    if (map) {
      map
        .getOverlays()
        .getArray()
        .slice()
        .forEach((ov) => {
          const el = ov.getElement();
          if (
            el &&
            (el.classList.contains('ol-tooltip-static') ||
              el.classList.contains('ol-tooltip-measure'))
          ) {
            map.removeOverlay(ov);
          }
        });
    }
  }, [stopMeasuring, map]);

  // Cleanup on unmount
  useEffect(() => {
    return () => removeInteraction();
  }, [removeInteraction]);

  return {
    startMeasuring,
    stopMeasuring,
    clearMeasurements,
    activeMeasureType,
  };
};
